import * as React from "react";
import { cx, css } from "@emotion/css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  BYOFundEntryInterface,
  BYOEntryInterface,
  PfFundSelectionBYOEntryError
} from "../support/interfaces/fundSelectionInterfaces";
import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import { PfTextPErcentage } from "./PfTextPercentage";

const faTimesC = faTimesCircle as IconProp;

interface PfFundSelectionBYOEntryProps {
  funds: BYOFundEntryInterface[];
  onFundSelectionChange: (fund: BYOFundEntryInterface | undefined) => void;
  onPercentChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onRemoveEntry: () => void;
  index: number;
  externalBindedValues: BYOEntryInterface;
  error: PfFundSelectionBYOEntryError;
  selectedFundCodeList: string[];
  setParentTouched: () => void;
}

export const PfFundSelectionBYOEntry = ({
  funds,
  onFundSelectionChange,
  onPercentChange,
  index,
  error,
  externalBindedValues,
  onRemoveEntry,
  selectedFundCodeList,
  setParentTouched
}: PfFundSelectionBYOEntryProps) => {
  const byoEntryClass = css`
    margin: 0 0 15px 0;
    padding: 15px 0;
  `;

  const backgroundColorNormal = css`
    background: #f6f7f7;
  `;

  const byoFundHeaderClass = css`
    font-size: 14px;
    font-weight: 700;
    color: #626767;
    margin: 0;
    padding: 0 0 10px 0;
  `;

  const noPadding = css`
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  `;

  const filteredFunds = funds.filter(fund => {
    if (
      selectedFundCodeList.includes(fund.fundCode) &&
      fund.fundCode !== externalBindedValues.fundSelect.fundCode
    ) {
      return false;
    }

    return true;
  });

  const [isTouchedFundSelect, setIsTouchedFundSelect] = React.useState(false);
  const [isTouchedFundPercent, setIsTouchedFundPercent] = React.useState(false);

  return (
    <div
      className={cx(
        bootstrapStyles.row,
        formGroupStyles["form-group"],
        byoEntryClass,
        {
          [backgroundColorNormal]: !(
            (isTouchedFundSelect || isTouchedFundPercent) &&
            (error.fundSelect.displayError || error.fundPercent.displayError)
          )
        },
        {
          [formGroupStyles.error]:
            (isTouchedFundSelect || isTouchedFundPercent) &&
            (error.fundSelect.displayError || error.fundPercent.displayError)
        }
      )}
    >
      <div className={bootstrapStyles["col-md-7"]}>
        <div className={bootstrapStyles.row}>
          <div className={bootstrapStyles["col-md-12"]}>
            <p className={byoFundHeaderClass}>Fund Name</p>
            <div className={formGroupStyles["input-group"]}>
              <select
                id={`fundSelect${index}`}
                style={{ width: "100%" }}
                value={externalBindedValues.fundSelect.fundName}
                data-testid="byoFundName"
                onChange={e => {
                  setIsTouchedFundSelect(true);
                  setParentTouched();

                  if (e.currentTarget.selectedIndex - 1 === 0) {
                    onFundSelectionChange(undefined);
                  }
                  onFundSelectionChange(
                    filteredFunds[e.currentTarget.selectedIndex - 1]
                  );
                }}
              >
                <option value="">Select</option>
                {filteredFunds.map(fund => {
                  return (
                    <option key={fund.fundCode} value={fund.fundName}>
                      {fund.fundName}
                    </option>
                  );
                })}
              </select>
              <div className={noPadding}>
                <div className={formGroupStyles["error-messages"]}>
                  {error.fundSelect.displayError && isTouchedFundSelect && (
                    <div>{error.fundSelect.errorMessage}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={bootstrapStyles["col-md-5"]}>
        <div className={bootstrapStyles.row}>
          <div
            className={cx(
              bootstrapStyles["col-md-9"],
              bootstrapStyles["col-sm-6"],
              bootstrapStyles["col-xs-5"]
            )}
          >
            <p className={byoFundHeaderClass}>Fund Percent</p>
            <PfTextPErcentage
              id={`fundSelectPercent${index}`}
              width="150px"
              externalBindedValue={externalBindedValues.fundPercent || ""}
              data-testid="byoFundPercent"
              min="1"
              max="100"
              onPercentChange={e => {
                onPercentChange(e);
                setParentTouched();
                setIsTouchedFundPercent(true);
              }}
              onBlur={() => {
                setIsTouchedFundPercent(true);
              }}
            />
            <div className={cx(formGroupStyles["error-messages"], noPadding)}>
              {error.fundPercent.displayError && isTouchedFundPercent && (
                <div>{error.fundPercent.errorMessage}</div>
              )}
            </div>
          </div>
          <div
            className={cx(
              bootstrapStyles["col-md-3"],
              bootstrapStyles["col-sm-6"],
              bootstrapStyles["col-xs-6"],
              css`
                text-align: center;
              `
            )}
          >
            <p className={byoFundHeaderClass}>&nbsp;</p>
            <button
              type="button"
              data-testid="removeFund"
              className={css`
                border: none;
                background-color: transparent;
                font-size: 25px;
              `}
              onClick={() => {
                onRemoveEntry();
              }}
            >
              <FontAwesomeIcon icon={faTimesC} style={{ color: "#007eb7" }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
